<script lang="ts" setup generic="T extends string | Component">
import {
  computed,
  useCssModule,
  useSlots,
  h,
  mergeProps,
  useAttrs,
  type Component,
  type VNodeChild,
} from 'vue';
import type { PolymorphicProps } from '../../shared/types';
const props = defineProps<
  PolymorphicProps<T> & {
    disabled?: boolean;
    active?: boolean;
    block?: boolean;
    expandToContent?: boolean;
    size?: 'm' | 'l';
    variant?: 'primary' | 'secondary' | 'tertiary';
  }
>();
defineSlots<{
  default?: () => VNodeChild;
  leadingVisual?: () => VNodeChild;
  trailingVisual?: () => VNodeChild;
}>();
const style = useCssModule();
const rootProps = computed(() => {
  return {
    class: [
      style.root,
      {
        [style.sizeL]: props.size === 'l',
        [style.variantSecondary]: props.variant === 'secondary',
        [style.variantTertiary]: props.variant === 'tertiary',
        [style.disabled]: props.disabled,
        [style.active]: props.active,
        [style.block]: props.block,
        [style.expandToContent]: props.expandToContent,
      },
    ],
    'data-ui-component': 'UiButton',
    'data-expand-content': props.expandToContent ? 'true' : undefined,
  };
});
const slots = useSlots();
const attrs = useAttrs();
defineRender(() => {
  const content = slots.default?.();
  const leadingVisual = slots.leadingVisual?.();
  const trailingVisual = slots.trailingVisual?.();
  const internalStructure = [
    leadingVisual ? h('div', { class: style.leadingVisual }, leadingVisual) : null,
    content,
    trailingVisual ? h('div', { class: style.trailingVisual }, trailingVisual) : null,
  ];
  if (props.as) {
    return h(props.as, mergeProps(attrs, rootProps.value, props.asProps ?? {}), {
      default: () => internalStructure,
    });
  }
  return h(
    'button',
    { ...rootProps.value, type: 'button', disabled: props.disabled },
    internalStructure,
  );
});
</script>

<style lang="scss" module>
@use '../../styles/shared';

.root {
  @include shared.reset-button();
  position: relative;
  display: inline-flex;
  gap: 4px;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  border-radius: 6px;
  height: 28px;
  flex: none;
  background: #226b6c;
  color: #f5f5f5;
  border: 1px solid #226b6c;
  padding: 0 (8px - 1px);
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;

  &:focus-visible {
    outline: 1px solid #085cd9;
    outline-offset: -1px;
  }

  &:hover:not(.disabled) {
    background:
      linear-gradient(0deg, rgba(35, 57, 93, 0.2) 0%, rgba(35, 57, 93, 0.2) 100%), #226b6c;
  }

  &:active:not(.disabled),
  &.active:not(.disabled) {
    background:
      linear-gradient(0deg, rgba(35, 57, 93, 0.2) 0%, rgba(35, 57, 93, 0.2) 100%),
      linear-gradient(0deg, rgba(35, 57, 93, 0.2) 0%, rgba(35, 57, 93, 0.2) 100%), #226b6c;
  }

  &:disabled,
  &.disabled {
    color: #d2d3d9;
    background: #ebecf2;
    border-color: #ebecf2;
    cursor: not-allowed;
  }
}

.leadingVisual,
.trailingVisual {
  width: 18px;
  height: 18px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  > img,
  > svg {
    max-width: 100%;
    max-height: 100%;
  }
}

.block {
  width: 100%;
}

.expandToContent {
  width: auto;
  max-width: none;
}

.sizeL {
  height: 36px;
  padding: 0 (16px - 1px);
}

.variantSecondary,
.variantTertiary {
  color: #010000;
  background: transparent;

  &:hover:not(.disabled) {
    background: rgba(35, 57, 93, 0.05);
  }

  &:active:not(.disabled),
  &.active:not(.disabled) {
    background: rgba(35, 57, 93, 0.1);
    color: #226b6c;
  }
}

.variantSecondary {
  border-color: #d5d6d9;
}

.variantTertiary {
  border-color: transparent;

  &.disabled {
    background: transparent;
    border-color: transparent;
  }
}
</style>
